import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  Popup,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ConfirmDisconnectOrgIntegrationView from 'src/app/ConfirmDisconnectOrgIntegrationView/ConfirmDisconnectOrgIntegrationView'
import DisconnectOrgIntegrationView from 'src/app/DisconnectOrgIntegrationView/DisconnectOrgIntegrationView'
import {
  deleteOrgIntegrationLogin,
  getIntegrationStatus,
  manageOrganizationAddAccountIntegration,
  startIntegrationConfiguration,
} from 'src/app/ManageOrgIntegration/redux/manageOrgIntegrationPopup.actions'
import {
  selectBankAccountsFromState,
  selectIntegrationIdFromRoute,
  selectIsAddingNetsuiteAccountTransactionSource,
  selectIsDeletingNetsuiteOrgIntegration,
  selectTaxRatesFromState,
  selectTopLevelManageOrgIntegrationErrorFromState,
} from 'src/app/ManageOrgIntegration/redux/manageOrgIntegrationPopup.selectors'
import AppName from 'src/components/AppName/AppName'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import InsetBox from 'src/components/InsetBox/InsetBox'
import IntegrationPopupFooter from 'src/components/IntegrationPopupFooter/IntegrationPopupFooter'
import IntegrationPopupHeader from 'src/components/IntegrationPopupHeader/IntegrationPopupHeader'
import LoadingPopupContent from 'src/components/LoadingPopupContent/LoadingPopupContent'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
} from 'src/enums'
import { useFlyoutDispatch, useFormFields, useInterval } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { BankAccountListModel } from 'src/models/bankAccount.models'
import { TaxRateListResponse } from 'src/models/taxRate.models'
import { closeModal } from 'src/navigation/Lnk'
import { useQueryOrgIntegration } from 'src/queries/organizations/integrations.queries'
import { createSetIsManageOrgIntegrationPopupOpen } from 'src/redux/actions/app.actions'
import { getOrgIntegration } from 'src/redux/actions/data/orgIntegrations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

const MAXIMUM_RETRIES = 5

const ManageOrgIntegration: React.FC<unknown> = (): React.ReactElement => {
  const dispatch = useFlyoutDispatch()
  const translate = useTranslate()

  const integrationId = useSelector(selectIntegrationIdFromRoute)
  const organizationId = useSelector(selectOrganizationIdFromRoute)

  const { data: orgIntegration, isFetched } = useQueryOrgIntegration({
    orgId: organizationId,
    orgIntegrationId: integrationId,
    enabled: !!organizationId && !!integrationId,
  })

  const bankAccounts = useSelector(selectBankAccountsFromState)
  const taxRates = useSelector(selectTaxRatesFromState)
  const loadingDelete = useSelector(selectIsDeletingNetsuiteOrgIntegration)
  const loadingAddAccount = useSelector(
    selectIsAddingNetsuiteAccountTransactionSource,
  )
  const topLevelError = useSelector(
    selectTopLevelManageOrgIntegrationErrorFromState,
  )
  const user = useSelector(selectUserListModelFromState)

  // const deletingOrgIntegrationLoading = useSelector(selectIsDeletingNetsuiteOrgIntegration)
  // const addingTransactionSourceLoading = useSelector(selectIsAddingNetsuiteAccountTransactionSource)

  const closePopup = (): void => {
    dispatch(createSetIsManageOrgIntegrationPopupOpen(false))
    dispatch(closeModal())
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [bankAccount, setBankAccount] = useState<
    BankAccountListModel | undefined
  >()
  const [taxRate, setTaxRate] = useState<TaxRateListResponse | undefined>()
  const connectedPlatform = orgIntegration?.connectedPlatform
  const showTaxRates =
    connectedPlatform !== undefined &&
    [
      OrganizationConnectedPlatform.DYNAMICS_BC,
      OrganizationConnectedPlatform.SAGE_INTACCT,
    ].includes(connectedPlatform)
  const hasBankAccountOptions = (bankAccounts?.length ?? 0) > 0
  const hasTaxRateOptions = (taxRates?.length ?? 0) > 0
  const {
    fieldsErrors: orgIntegrationFieldsErrors,
    onFieldChange: onOrgIntegrationFieldChange,
    setFieldsErrors: setOrgIntegrationFieldsErrors,
    setErrorsAndReturnDidPassValidation:
      orgIntegrationFieldsSetErrorsAndValidate,
  } = useFormFields<{
    bankAccountId?: string
    taxRateId?: string
  }>({}, () => ({
    bankAccountId: [stringIsEmpty()],
    taxRateId:
      showTaxRates && hasTaxRateOptions ? [stringIsEmpty()] : undefined,
  }))
  const [fetchedInitialBankAccounts, setFetchedInitialBankAccounts] =
    useState(false)
  const [syncingConnections, setSyncingConnections] = useState(true)
  const [retries, setRetries] = useState(0)
  const bankAccountOptions =
    bankAccounts?.map((account) => ({
      value: account.id,
      label: account.accountName,
    })) ?? []
  const taxRateOptions =
    taxRates?.map((taxRate) => {
      const displayName = taxRate.name || taxRate.code || taxRate.id
      const displayRate = `${String(taxRate.effectiveTaxRate)}%`
      return {
        value: taxRate.id,
        label: `${displayRate} - ${displayName}`,
      }
    }) ?? []
  const showAccountSelection = !syncingConnections && hasBankAccountOptions
  const showTaxRateSelection = !syncingConnections && hasTaxRateOptions

  // fetching accounts usually takes at least 3-5 seconds, so we ping the backend for status before starting config
  useInterval(
    () => {
      if (
        orgIntegration &&
        [
          OrganizationIntegrationStatus.WAITING_FOR_INITIAL_PULL,
          OrganizationIntegrationStatus.WAITING_TO_SYNC,
        ].includes(orgIntegration.connectionStatus)
      ) {
        dispatch(
          getIntegrationStatus(
            orgIntegration.organizationId,
            orgIntegration.id,
            () => setRetries((retries) => retries + 1),
          ),
        )
      }
    },
    syncingConnections ? 3000 : null,
  )

  // user hasn't selected a bank account so fetch list
  useEffect(() => {
    if (
      orgIntegration &&
      OrganizationIntegrationStatus.PENDING_CONFIGURATION ===
        orgIntegration.connectionStatus &&
      !orgIntegration.bankAccount &&
      !fetchedInitialBankAccounts
    ) {
      dispatch(
        startIntegrationConfiguration(
          orgIntegration.organizationId,
          orgIntegration.id,
        ),
      )
      setFetchedInitialBankAccounts(true)
    }

    // move to page 2 if successfully added a bank account
    if (orgIntegration && orgIntegration.bankAccount && currentPage === 1) {
      setBankAccount(orgIntegration.bankAccount)
      onOrgIntegrationFieldChange(
        'bankAccountId',
        orgIntegration.bankAccount.id,
      )
      setCurrentPage(2)
    }
  }, [orgIntegration])

  useEffect(() => {
    if (
      bankAccounts ||
      (orgIntegration &&
        OrganizationIntegrationStatus.PENDING_CONFIGURATION ===
          orgIntegration.connectionStatus) ||
      retries > MAXIMUM_RETRIES
    ) {
      setSyncingConnections(false)
    }
  }, [bankAccounts, retries])

  useEffect(() => {
    // get org integration detail on page load
    dispatch(getOrgIntegration(organizationId, integrationId))

    dispatch(createSetIsManageOrgIntegrationPopupOpen(true))
  }, [])

  const handleDisconnectIntegration = (): void => {
    if (orgIntegration) {
      dispatch(
        deleteOrgIntegrationLogin(
          organizationId,
          integrationId,
          orgIntegration.platformName,
        ),
      )
    }
  }

  const addIntegrationDetails = (
    bankAccount: BankAccountListModel | undefined,
    taxRateId?: string,
  ): void => {
    if (!bankAccount || !orgIntegration) return
    setBankAccount(bankAccount)
    dispatch(
      manageOrganizationAddAccountIntegration(
        organizationId,
        integrationId,
        bankAccount.id,
        showTaxRates ? taxRateId : undefined,
        orgIntegration.platformName,
      ),
    )
  }

  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
        padding: 0;
      `}
      onClickClose={closePopup}
      showCloseButton
    >
      {!isFetched ? (
        <LoadingPopupContent />
      ) : !(
          user?.hasOrgBookkeeperPermissions &&
          user?.organizationId === organizationId &&
          orgIntegration &&
          orgIntegration.organizationId === organizationId &&
          orgIntegration.id === integrationId
        ) ? (
        <NotFoundPopupContent />
      ) : (
        <Fragment>
          <IntegrationPopupHeader
            title={<>{orgIntegration.platformName}</>}
            subtitle={
              currentPage === 1 ? (
                <Trns
                  source={orgIntegration.connectionMessage}
                  expressions={{
                    appName: <AppName />,
                    integration: orgIntegration.connectedPlatform,
                  }}
                />
              ) : (
                <Trns
                  source={'Disconnect from ${connectedPlatform}.'}
                  expressions={{
                    connectedPlatform: orgIntegration.platformName,
                  }}
                />
              )
            }
            iconSrc={orgIntegration.logo}
            iconElement={orgIntegration.iconElement}
          />
          {/* Choose Account to Push Expenses to */}
          {currentPage === 1 && (
            <Fragment>
              <div
                css={css`
                  padding: 30px 60px 30px;
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    padding: 40px;
                  `)};
                  ${getBreakpointDown(breakpointSizes.XXSM)(css`
                    padding: 40px 20px;
                  `)};
                `}
              >
                {showAccountSelection && (
                  <p
                    css={css`
                      ${getFontCss(
                        20,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      padding-bottom: 16px;
                    `}
                  >
                    <Trns
                      source={
                        'Finalize the details to complete your connection'
                      }
                    />
                  </p>
                )}

                {/* View for attempting to sync accounts via pinging */}
                {syncingConnections && (
                  <div
                    css={css`
                      margin-top: 12px;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      `}
                    >
                      <InsetBox
                        css={css`
                          background-color: ${styleConstants.colors.gray_light};
                          width: 100%;
                          align-items: center;
                          text-align: center;
                          justify-content: space-between;
                          padding: 40px;
                        `}
                      >
                        <Processing
                          css={css`
                            margin-top: 10px;
                          `}
                        />
                        <p
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                            margin-top: 12px;
                          `}
                        >
                          <Trns
                            source={'Please wait while we sync your accounts'}
                          />
                        </p>
                      </InsetBox>
                    </div>
                  </div>
                )}
                {/* View for when pinging sync fails */}
                {!syncingConnections && !bankAccounts && (
                  <div
                    css={css`
                      margin-top: 12px;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      `}
                    >
                      <InsetBox
                        css={css`
                          background-color: ${styleConstants.colors.gray_light};
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          align-items: center;
                          justify-content: space-between;
                          padding: 40px;
                        `}
                      >
                        <p
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )};
                          `}
                        >
                          <Trns
                            source={
                              'Sync is in progress and may take several hours.'
                            }
                          />
                        </p>
                        <Button
                          css={css`
                            margin-top: 10px;
                          `}
                          design={ButtonDesigns.designCta}
                          type={ButtonTypes.button}
                          text={<Trns source={'Check Sync Status'} />}
                          onClick={(): void => {
                            setSyncingConnections(true)
                            setRetries(0)
                          }}
                        />
                      </InsetBox>
                    </div>
                  </div>
                )}
                {/* Empty state */}
                {!syncingConnections && bankAccounts?.length === 0 ? (
                  <div
                    css={css`
                      margin-top: 12px;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                      `}
                    >
                      <InsetBox
                        css={css`
                          background-color: ${styleConstants.colors.gray_light};
                          width: 100%;
                          display: flex;
                          align-items: center;
                          text-align: center;
                          justify-content: space-between;
                          padding: 40px;
                        `}
                      >
                        <div>
                          <p
                            css={css`
                              margin-bottom: 20px;
                              ${getFontCss(
                                18,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.medium,
                              )};
                            `}
                          >
                            <Trns
                              source={
                                'You have no Accounts on ${connectedPlatform}'
                              }
                              expressions={{
                                connectedPlatform: orgIntegration.platformName,
                              }}
                            />
                          </p>
                          <p
                            css={css`
                              margin-bottom: 12px;
                              ${getFontCss(
                                16,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            <Trns
                              source={
                                'You first need to log into ${connectedPlatform} and add your accounts there.'
                              }
                              expressions={{
                                connectedPlatform: orgIntegration.platformName,
                              }}
                            />
                          </p>
                          <p
                            css={css`
                              ${getFontCss(
                                16,
                                styleConstants.colors.gray_darkest,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            <Trns
                              source={
                                "Once that is done, come back here and restart connection process (you won't need to log back in)."
                              }
                            />
                          </p>
                        </div>
                      </InsetBox>
                    </div>
                  </div>
                ) : showAccountSelection ? (
                  <div>
                    <SingleDropdown
                      data-testid={'selectBankAccount'}
                      label={translate('Bank Account')}
                      options={bankAccountOptions}
                      selectedValue={
                        bankAccount
                          ? {
                              value: bankAccount.id,
                              label: bankAccount.accountName,
                            }
                          : undefined
                      }
                      onSelect={({ value }): void => {
                        const selectedAccount = bankAccounts?.find(
                          (account) => account.id === value,
                        )
                        setBankAccount(selectedAccount)
                        onOrgIntegrationFieldChange('bankAccountId', value)
                        setOrgIntegrationFieldsErrors({
                          ...orgIntegrationFieldsErrors,
                          bankAccountId: undefined,
                        })
                      }}
                      placeholder={translate('Select from list')}
                      dropdownOptionsCount={4}
                      noneLabel={''}
                      errorText={orgIntegrationFieldsErrors['bankAccountId']}
                      requirement={'required'}
                      disabled={
                        syncingConnections || bankAccountOptions.length === 0
                      }
                    />
                    <span
                      css={css`
                        display: inline-block;
                        margin-top: 8px;
                        padding: 0px 20px;
                        ${getFontCss(
                          12,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'If you do not see the right account, you need to log into ${connectedPlatform} and add it there first.'
                        }
                        expressions={{
                          connectedPlatform: orgIntegration.platformName,
                        }}
                      />
                    </span>
                  </div>
                ) : null}
                {showTaxRateSelection && showTaxRates && (
                  <div
                    css={css`
                      margin-top: 26px;
                    `}
                  >
                    <SingleDropdown
                      data-testid={'selectTaxRate'}
                      label={translate('Tax Rate')}
                      options={taxRateOptions}
                      selectedValue={
                        taxRate
                          ? {
                              value: taxRate.id,
                              label: `${String(
                                taxRate.effectiveTaxRate,
                              )}% - ${taxRate.name || taxRate.code || taxRate.id}`,
                            }
                          : undefined
                      }
                      onSelect={({ value }): void => {
                        const selectedTaxRate = taxRates?.find(
                          (rate) => rate.id === value,
                        )
                        setTaxRate(selectedTaxRate)
                        onOrgIntegrationFieldChange('taxRateId', value)
                        setOrgIntegrationFieldsErrors({
                          ...orgIntegrationFieldsErrors,
                          taxRateId: undefined,
                        })
                      }}
                      placeholder={translate('Select from list')}
                      dropdownOptionsCount={4}
                      noneLabel={''}
                      errorText={orgIntegrationFieldsErrors['taxRateId']}
                      requirement={'required'}
                      disabled={
                        syncingConnections || taxRateOptions.length === 0
                      }
                    />
                    <span
                      css={css`
                        display: inline-block;
                        margin-top: 8px;
                        padding: 0px 20px;
                        ${getFontCss(
                          12,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'Choose the tax rate that will be applied to all ${appName} transactions. This tax rate cannot be changed per transaction in ${appName}.'
                        }
                        expressions={{
                          appName: <AppName />,
                        }}
                      />
                    </span>
                  </div>
                )}
                {orgIntegration.connectionStatus ===
                  OrganizationIntegrationStatus.PENDING_CONFIGURATION && (
                  <div
                    css={css`
                      padding-top: 30px;
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <Button
                      design={ButtonDesigns.designCta}
                      type={ButtonTypes.button}
                      big
                      text={<Trns source={'Confirm and Continue'} />}
                      onClick={(): void => {
                        const didPassValidation =
                          orgIntegrationFieldsSetErrorsAndValidate()
                        if (!didPassValidation) return
                        addIntegrationDetails(bankAccount, taxRate?.id)
                      }}
                      loading={loadingAddAccount}
                      disabled={
                        bankAccounts?.length === 0 ||
                        bankAccount === null ||
                        loadingDelete
                      }
                    />
                    <HoverableLink
                      css={css`
                        display: flex;
                        align-items: center;
                        padding-left: 20px;
                      `}
                      ariaLabelSource={'Cancel Connection'}
                      onClick={handleDisconnectIntegration}
                      loading={loadingDelete}
                    >
                      <Trns source={'Cancel Connection'} />
                    </HoverableLink>
                  </div>
                )}
                {topLevelError !== undefined && (
                  <div
                    css={css`
                      padding-top: 20px;
                      display: inline-block;
                      ${getFontCss(
                        16,
                        styleConstants.colors.red_dark,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    <Trns source={topLevelError} />
                  </div>
                )}
              </div>
              {bankAccounts && bankAccounts.length > 0 && (
                <IntegrationPopupFooter
                  css={css`
                    background-color: ${styleConstants.colors.white};
                    padding-top: 0px;
                  `}
                >
                  <div
                    css={css`
                      ${getFontCss(
                        14,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      margin-bottom: 12px;
                    `}
                  >
                    <Trns source={'Take Note'} />
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                    `}
                  >
                    <SvgAlertCircledFilled
                      css={css`
                        transform: scale(calc(14 / 24));
                        transform-origin: top;
                        flex-shrink: 0;
                        margin-right: 6px;
                        align-items: center;
                      `}
                    />
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.font_gray_light,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'This will replace all existing ${appName} Expense Categories with ${connectedPlatform}. These will be read-only on ${appName}.'
                        }
                        expressions={{
                          appName: <AppName />,
                          connectedPlatform: orgIntegration.platformName,
                        }}
                      />
                    </span>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                    `}
                  >
                    <SvgAlertCircledFilled
                      css={css`
                        transform: scale(calc(14 / 24));
                        transform-origin: top;
                        flex-shrink: 0;
                        margin-right: 6px;
                        align-items: center;
                      `}
                    />
                    <span
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.font_gray_light,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'All new, cleared transactions on your ${appName} profile can manually be pushed to ${connectedPlatform}.'
                        }
                        expressions={{
                          appName: <AppName />,
                          connectedPlatform: orgIntegration.platformName,
                        }}
                      />
                    </span>
                  </div>
                  {showTaxRates && (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                      `}
                    >
                      <SvgAlertCircledFilled
                        css={css`
                          transform: scale(calc(14 / 24));
                          transform-origin: top;
                          flex-shrink: 0;
                          margin-right: 6px;
                          align-items: center;
                        `}
                      />
                      <span
                        css={css`
                          ${getFontCss(
                            12,
                            styleConstants.colors.font_gray_light,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        <Trns
                          source={
                            'A tax rate is required to finalize your ${connectedPlatform} connection. ${appName} does not manage tax calculation. This selection ensures transactions can be successfully synced.'
                          }
                          expressions={{
                            appName: <AppName />,
                            connectedPlatform: orgIntegration.platformName,
                          }}
                        />
                      </span>
                    </div>
                  )}
                </IntegrationPopupFooter>
              )}
            </Fragment>
          )}

          {/* Disconnect Transaction Source */}
          {currentPage === 2 && bankAccount && (
            <DisconnectOrgIntegrationView
              bankAccount={bankAccount}
              disconnectAccount={(): void => {
                setCurrentPage(3)
              }}
              connectedPlatformName={orgIntegration.platformName}
            />
          )}

          {/* Disconnect Org Integration */}
          {currentPage === 3 && bankAccount && (
            <ConfirmDisconnectOrgIntegrationView
              goBack={(): void => {
                setCurrentPage(2)
              }}
              disconnectIntegration={handleDisconnectIntegration}
              loading={loadingDelete}
              connectedPlatformName={orgIntegration.platformName}
            />
          )}
        </Fragment>
      )}
    </Popup>
  )
}

export default ManageOrgIntegration
